import type { Href } from "expo-router";
import { COLORS } from "../../lib/brand";
import { Link, useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useMemo, useState } from "react";
import {
  FlatList,
  Modal,
  Platform,
  Pressable,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from "react-native";

import Svg, { Path } from "react-native-svg";

import { AuthCard } from "./AuthCard";
import { Button, Input, PasswordStrengthMeter, Pill, passwordMeetsMinimumStrength } from "../ui";
import { getEmailDomain, isEmailDomainAllowed, listAllowedDomains, matchEmailDomain } from "../../lib/emailWhitelist";
import {
  acceptInvite,
  clearPendingInviteToken,
  getPendingInviteToken,
  portalHrefAfterAccept,
  setPendingInviteToken,
} from "../../lib/invites";
import { supabase } from "../../lib/supabase";
import { majorsForSchools } from "../../lib/majors";
import type { AllowedEmailDomain } from "../../types/database";

function firstParam(v: string | string[] | undefined): string | undefined {
  if (v == null) return undefined;
  return Array.isArray(v) ? v[0] : v;
}

const FOREST = COLORS.FOREST.hex;
const AMBER = COLORS.AMBER.hex;
const SAGE = COLORS.LIVE_GREEN.hex;
const SUBTITLE = "rgba(28,43,26,0.68)";
const SUCCESS = "#3a9c42";
const WARN = "#c47a1a";
const RED_MISMATCH = "#C0392B";
const GREEN_MATCH = "#5FAD56";
const PASSWORD_PLACEHOLDER = "8+ characters, including uppercase, digit, and symbol";

const PROGRAM_LEVELS = ["Undergraduate", "Graduate", "MBA", "Doctoral"] as const;
export type ProgramLevel = (typeof PROGRAM_LEVELS)[number];

const GRAD_TERMS = ["Spring", "Summer", "Fall"] as const;

const FIELDS_OF_COMPETENCY = [
  "Business & Finance",
  "Marketing & Communications",
  "Technology & Computer Science",
  "Data Science & Analytics",
  "Engineering",
  "Sciences & Mathematics",
  "Health & Medicine",
  "Social Sciences",
  "Law & Public Policy",
  "Arts & Design",
  "Humanities & Liberal Arts",
  "Education",
  "Environmental & Sustainability",
] as const;

export function StudentSignupForm() {
  const router = useRouter();
  const { width: winW } = useWindowDimensions();
  // Pair Program Level + Fields of Competency into a single row once the
  // card has room for it. The AuthCard itself caps at 760px when `wide`,
  // so the breakpoint here is the viewport, not the card width.
  const sideBySide = winW >= 800;
  const params = useLocalSearchParams<{ email?: string | string[]; token?: string | string[] }>();
  const paramEmail = firstParam(params.email);
  const paramToken = firstParam(params.token);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [university, setUniversity] = useState("");
  const [programLevel, setProgramLevel] = useState<ProgramLevel | "">("");
  const [competency, setCompetency] = useState<Set<string>>(() => new Set());
  const [graduationTerm, setGraduationTerm] = useState<(typeof GRAD_TERMS)[number] | "">("");
  const [graduationYear, setGraduationYear] = useState("");
  const [allowedDomains, setAllowedDomains] = useState<AllowedEmailDomain[]>([]);
  const [domainsLoaded, setDomainsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState<string | null>(null);
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  // University is now detected from the email domain — no manual
  // picker. The `university` state still feeds the submit payload;
  // see the domainMatch effect that fills it in.

  // Majors — multi-select, filtered by the detected university so a
  // UF student only sees UF programs. Locked until the email matches
  // a partner school (we can't pick the right list otherwise).
  const [majors, setMajors] = useState<Set<string>>(() => new Set());
  const [majorModalOpen, setMajorModalOpen] = useState(false);
  const [majorSearch, setMajorSearch] = useState("");
  // Fields-of-competency picker — same modal+search pattern as the
  // university picker just above. Opens an overlay with a typeahead
  // and a checklist of all 13 fields; toggles directly into the
  // existing `competency` Set so the rest of the form is unchanged.
  const [compModalOpen, setCompModalOpen] = useState(false);
  const [compSearch, setCompSearch] = useState("");
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    let mounted = true;
    void (async () => {
      const stored = await getPendingInviteToken();
      if (!mounted) return;
      const t = paramToken ?? stored ?? null;
      setInviteToken(t);
      if (paramEmail) setEmail(paramEmail.trim().toLowerCase());
    })();
    return () => {
      mounted = false;
    };
  }, [paramEmail, paramToken]);

  const emailLocked = !!paramEmail;

  useEffect(() => {
    let mounted = true;
    void (async () => {
      const rows = await listAllowedDomains();
      if (!mounted) return;
      setAllowedDomains(rows);
      setDomainsLoaded(true);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const universityOptions = useMemo(
    () =>
      Array.from(new Set(allowedDomains.map((d) => d.university_name.trim()).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [allowedDomains]
  );

  // The university options array is no longer used by any picker — the
  // `university` value is auto-detected from the email domain. Keep
  // `universityOptions` in case future code wants the full list.
  void universityOptions;

  const domainMatch = useMemo(() => {
    const e = email.trim();
    if (!e.includes("@")) return null;
    return matchEmailDomain(e, allowedDomains);
  }, [email, allowedDomains]);

  const emailHasDomain = !!getEmailDomain(email.trim());

  const passwordOk = passwordMeetsMinimumStrength(password);
  const confirmFilled = confirmPassword.length > 0;
  const passwordsMatch = password === confirmPassword;
  const canSubmitAccount = passwordOk && confirmFilled && passwordsMatch;

  useEffect(() => {
    if (domainMatch?.university_name) setUniversity(domainMatch.university_name);
  }, [domainMatch?.university_name]);

  useEffect(() => {
    if (canSubmitAccount) setSubmitAttempted(false);
  }, [canSubmitAccount]);

  function toggleCompetency(label: string) {
    setCompetency((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }

  function toggleMajor(label: string) {
    setMajors((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }

  // Majors available for the detected school. When no university has
  // been resolved yet (email blank or domain unknown), this stays
  // empty and the picker is disabled — there's no useful list to show
  // until we know which school's catalog applies.
  const availableMajors = useMemo<ReadonlyArray<string>>(() => {
    if (!university.trim()) return [];
    return majorsForSchools([university.trim()]);
  }, [university]);

  // Drop majors that are no longer in scope when the university changes
  // (e.g. user re-types email and a different school is detected).
  useEffect(() => {
    if (!availableMajors.length) {
      if (majors.size > 0) setMajors(new Set());
      return;
    }
    const allowed = new Set(availableMajors);
    let changed = false;
    const next = new Set<string>();
    majors.forEach((m) => {
      if (allowed.has(m)) next.add(m);
      else changed = true;
    });
    if (changed) setMajors(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableMajors]);

  async function onSubmit() {
    setError(null);
    setConfirmMsg(null);

    if (!domainsLoaded) {
      setError("Validating school email domains. Please try again in a moment.");
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      setError("Please enter your first and last name.");
      return;
    }
    if (!isEmailDomainAllowed(email.trim(), allowedDomains)) {
      const d = getEmailDomain(email.trim()) || "That email domain";
      setError(
        `Coravu requires a valid school email. ${d} isn't on our partner list. Contact Coravu if your school should be added.`
      );
      return;
    }
    if (!university.trim()) {
      setError("Please select your university.");
      return;
    }
    if (!programLevel) {
      setError("Please select your program level.");
      return;
    }
    if (competency.size === 0) {
      setError("Select at least one field of competency.");
      return;
    }
    if (majors.size === 0) {
      setError("Select at least one major from your school's catalog.");
      return;
    }
    if (!graduationTerm) {
      setError("Please select your estimated graduation term.");
      return;
    }
    const gy = parseInt(graduationYear.trim(), 10);
    if (!Number.isFinite(gy) || graduationYear.trim().length !== 4) {
      setError("Graduation year must be a 4-digit year.");
      return;
    }
    if (!canSubmitAccount) {
      setSubmitAttempted(true);
      if (!passwordOk) {
        setError("Password must be at least 8 characters and meet minimum strength.");
      } else if (!confirmFilled) {
        setError("Confirm your password to continue.");
      } else {
        setError("Passwords do not match.");
      }
      return;
    }

    const fullName = `${firstName.trim()} ${lastName.trim()}`;
    const fieldsArr = Array.from(competency);

    setBusy(true);
    try {
      const { data, error: signErr } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          data: {
            coravu_role: "student",
            full_name: fullName,
            university: university.trim(),
            program_level: programLevel,
            fields_of_competency: fieldsArr,
            graduation_term: graduationTerm,
            graduation_year: gy,
          },
        },
      });
      if (signErr) {
        setError(signErr.message);
        return;
      }
      if (!data.session) {
        if (inviteToken) await setPendingInviteToken(inviteToken);
        setConfirmMsg("Check your email to confirm your account, then sign in.");
        return;
      }

      if (inviteToken) {
        try {
          const result = await acceptInvite(inviteToken);
          await clearPendingInviteToken();
          router.replace(portalHrefAfterAccept(result));
          return;
        } catch (e) {
          setError(e instanceof Error ? e.message : "Invite could not be applied.");
          return;
        }
      }

      const { error: completeErr } = await supabase.rpc("complete_student_signup", {
        p_full_name: fullName,
        p_university: university.trim(),
        // p_major is a single text column today — pack the multi-select
        // into a comma-joined string so we don't drop information until
        // the schema can carry an array. The list is already filtered to
        // the school's catalog so we don't have to worry about garbage.
        p_major: Array.from(majors).join(", ") || null,
        p_graduation_year: gy,
        p_year_in_school: null,
        p_program_level: programLevel,
        p_graduation_term: graduationTerm,
        p_fields_of_competency: fieldsArr,
      });
      if (completeErr) {
        setError(completeErr.message);
        return;
      }
      router.replace("/student" as Href);
    } finally {
      setBusy(false);
    }
  }

  const emailInputValid = emailHasDomain && !!domainMatch;
  const emailInputWarn = emailHasDomain && !domainMatch;

  return (
    <AuthCard wide>
      <View className="mb-4 flex-row border-b border-sand-dark">
        <Pressable
          onPress={() => router.push("/login" as Href)}
          className="pb-2.5 pr-4 pt-2"
          style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {}}
        >
          <Text className="text-[11px] font-bold uppercase tracking-wide text-text/45" style={{ fontFamily: "DMSans_700Bold" }}>
            SIGN IN
          </Text>
        </Pressable>
        <Pressable className="pb-2.5 pl-2 pr-2 pt-2">
          <Text
            className="text-[11px] font-bold uppercase tracking-wide"
            style={{
              fontFamily: "DMSans_700Bold",
              color: FOREST,
              borderBottomWidth: 2,
              borderBottomColor: AMBER,
              paddingBottom: 8,
              marginBottom: -1,
            }}
          >
            CREATE ACCOUNT
          </Text>
        </Pressable>
      </View>

      <Text className="text-[20px] leading-tight text-text" style={{ fontFamily: "CormorantGaramond_400Regular" }}>
        Create your account.
      </Text>
      <Text className="mt-1 text-[13px] leading-5" style={{ fontFamily: "DMSans_300Light", color: SUBTITLE }}>
        Join with your school email and start building a proof-first recruiting profile.
      </Text>

      <View className="mt-4 flex-row flex-wrap gap-3">
        <View className="min-w-[140px] flex-1">
          <Input label="First name" value={firstName} onChangeText={setFirstName} placeholder="Alex" required />
        </View>
        <View className="min-w-[140px] flex-1">
          <Input label="Last name" value={lastName} onChangeText={setLastName} placeholder="Johnson" required />
        </View>
      </View>

      <View className="mt-3.5">
        <Input
          label="University email"
          keyboardType="email-address"
          value={email}
          onChangeText={setEmail}
          placeholder="you@university.edu"
          helper={emailLocked ? "Locked to invite email" : undefined}
          editable={!emailLocked}
          valid={emailInputValid}
          warn={emailInputWarn}
          required
        />
        {emailInputValid && domainMatch ? (
          <Text className="mt-1 text-[12px]" style={{ fontFamily: "DMSans_500Medium", color: SUCCESS }}>
            ✓ {domainMatch.university_name} recognised
          </Text>
        ) : emailInputWarn ? (
          <View style={{ marginTop: 4, gap: 4 }}>
            <Text className="text-[12px]" style={{ fontFamily: "DMSans_500Medium", color: WARN }}>
              This domain is not on our partner list yet.
            </Text>
            <Text
              className="text-[12px]"
              style={{ fontFamily: "DMSans_500Medium", color: AMBER }}
              onPress={() => {
                if (Platform.OS === "web" && typeof window !== "undefined") {
                  const subject = encodeURIComponent("Add my school to Coravu");
                  const body = encodeURIComponent(
                    `Hi Coravu — please add my school.\n\nSchool name: \nSchool email domain: ${getEmailDomain(email.trim()) || ""}\nCity / state: \nMy email: ${email.trim()}\nMy name: ${firstName} ${lastName}\n`,
                  );
                  window.location.href = `mailto:admin@coravu.com?subject=${subject}&body=${body}`;
                }
              }}
            >
              Request to add your school →
            </Text>
          </View>
        ) : (
          <Text className="mt-1 min-h-[16px] text-[12px] text-transparent"> </Text>
        )}
      </View>

      {/* University is auto-resolved from the email domain (the
          domainMatch effect above writes to `university`). We render
          a quiet read-only line so the user can confirm what we
          detected without forcing them to pick a school manually. */}
      {university ? (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
            paddingHorizontal: 12,
            paddingVertical: 8,
            marginTop: 6,
            borderRadius: 6,
            backgroundColor: "rgba(100,127,77,0.10)",
            borderWidth: 1,
            borderColor: "rgba(100,127,77,0.45)",
          }}
        >
          <Svg width={12} height={12} viewBox="0 0 12 12" fill="none">
            <Path
              d="M2 6.5 L4.8 9 L10 3.5"
              stroke="#3F5A30"
              strokeWidth={1.7}
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </Svg>
          <Text
            style={{
              fontFamily: "DMSans_500Medium",
              fontSize: 12,
              color: "#3F5A30",
            }}
          >
            Detected from email: <Text style={{ fontFamily: "DMSans_700Bold" }}>{university}</Text>
          </Text>
        </View>
      ) : null}

      {/* Majors — multi-select dropdown filtered by the detected school.
          Disabled until the email matches a partner school so we know
          which catalog to show. Same modal pattern as Fields of
          competency below. */}
      <Text className="mb-0.5 mt-3.5 text-[10px] font-bold uppercase tracking-[0.16em] text-text/65">
        Majors<Text style={{ color: "#C0392B" }}> *</Text>
      </Text>
      <Text className="mb-1.5 text-[11.5px] text-text/55" style={{ fontFamily: "DMSans_400Regular" }}>
        {availableMajors.length > 0
          ? `Pick one or more from your school's catalog (${university}).`
          : "Enter your school email above first — we'll show only your school's majors."}
      </Text>
      <Pressable
        onPress={() => {
          if (availableMajors.length === 0) return;
          setMajorSearch("");
          setMajorModalOpen(true);
        }}
        disabled={availableMajors.length === 0}
        style={({ hovered }: { hovered?: boolean }) => [
          {
            minHeight: 44,
            borderRadius: 6,
            borderWidth: 1,
            borderColor:
              availableMajors.length === 0
                ? "#E5E0D5"
                : hovered && Platform.OS === "web"
                ? "rgba(28,43,26,0.42)"
                : "#D9D2C5",
            backgroundColor: availableMajors.length === 0 ? "rgba(28,43,26,0.03)" : COLORS.WHITE.hex,
            paddingHorizontal: 12,
            paddingVertical: 10,
            flexDirection: "row",
            alignItems: "center",
            gap: 10,
          },
          Platform.OS === "web"
            ? ({ cursor: availableMajors.length === 0 ? "not-allowed" : "pointer" } as unknown as object)
            : null,
        ]}
      >
        <View style={{ flex: 1, flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
          {majors.size === 0 ? (
            <Text
              style={{
                fontFamily: "DMSans_400Regular",
                fontSize: 13,
                color: "rgba(28,43,26,0.42)",
              }}
            >
              {availableMajors.length === 0 ? "Locked — fill in email first" : "Choose your major(s)…"}
            </Text>
          ) : (
            availableMajors
              .filter((m) => majors.has(m))
              .map((m) => (
                <View
                  key={m}
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 4,
                    paddingHorizontal: 8,
                    paddingVertical: 3,
                    borderRadius: 999,
                    backgroundColor: "rgba(100,127,77,0.14)",
                    borderWidth: 1,
                    borderColor: "rgba(100,127,77,0.45)",
                  }}
                >
                  <Text
                    style={{
                      fontFamily: "DMSans_500Medium",
                      fontSize: 11.5,
                      color: "#3F5A30",
                    }}
                  >
                    {m}
                  </Text>
                </View>
              ))
          )}
        </View>
        <Svg width={11} height={11} viewBox="0 0 12 12" fill="none">
          <Path
            d="M2 4 L6 8 L10 4"
            stroke="rgba(28,43,26,0.55)"
            strokeWidth={1.4}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </Svg>
      </Pressable>

      <Modal
        visible={majorModalOpen}
        animationType="none"
        transparent
        onRequestClose={() => setMajorModalOpen(false)}
      >
        <Pressable
          onPress={() => setMajorModalOpen(false)}
          style={[
            {
              flex: 1,
              backgroundColor: "rgba(14,14,14,0.50)",
              alignItems: "center",
              justifyContent: "center",
              paddingHorizontal: 24,
            },
            Platform.OS === "web"
              ? ({
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                  cursor: "default",
                } as object)
              : null,
          ]}
        >
          <Pressable
            onPress={(e) => e.stopPropagation?.()}
            style={[
              {
                width: "100%",
                maxWidth: 460,
                maxHeight: "82%",
                backgroundColor: COLORS.LINEN.hex,
                borderRadius: 8,
                borderWidth: 1,
                borderColor: "#D9D2C5",
                overflow: "hidden",
              },
              Platform.OS === "web" ? ({ cursor: "default" } as unknown as object) : null,
            ]}
          >
            <View
              style={{
                paddingHorizontal: 16,
                paddingVertical: 12,
                borderBottomWidth: 1,
                borderBottomColor: "#D9D2C5",
                flexDirection: "row",
                alignItems: "center",
                gap: 10,
              }}
            >
              <View style={{ flex: 1, alignItems: "flex-start" }}>
                <View
                  style={{
                    paddingHorizontal: 10,
                    paddingVertical: 4,
                    borderRadius: 999,
                    borderWidth: 1,
                    borderColor: "rgba(127,203,122,0.45)",
                    backgroundColor: "rgba(127,203,122,0.18)",
                  }}
                >
                  <Text
                    style={{
                      fontFamily: "DMSans_700Bold",
                      fontSize: 10,
                      letterSpacing: 1.4,
                      textTransform: "uppercase",
                      color: SAGE,
                    }}
                  >
                    Majors
                  </Text>
                </View>
                <Text
                  style={{
                    fontFamily: "DMSans_400Regular",
                    fontSize: 11,
                    color: "rgba(28,43,26,0.55)",
                    marginTop: 4,
                  }}
                  numberOfLines={1}
                >
                  {university}
                </Text>
              </View>
              <Pressable
                onPress={() => setMajorModalOpen(false)}
                accessibilityRole="button"
                accessibilityLabel="Close"
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                style={[
                  {
                    width: 26,
                    height: 26,
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 4,
                    borderWidth: 1,
                    borderColor: "rgba(28,43,26,0.18)",
                  },
                  Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                <Svg width={10} height={10} viewBox="0 0 12 12" fill="none">
                  <Path
                    d="M2 2 L10 10 M10 2 L2 10"
                    stroke={FOREST}
                    strokeWidth={1.4}
                    strokeLinecap="round"
                  />
                </Svg>
              </Pressable>
            </View>
            <View style={{ paddingHorizontal: 12, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: "#D9D2C5" }}>
              <TextInput
                value={majorSearch}
                onChangeText={setMajorSearch}
                placeholder="Search majors…"
                placeholderTextColor="rgba(28,43,26,0.42)"
                autoFocus
                style={{
                  paddingHorizontal: 12,
                  paddingVertical: 9,
                  borderRadius: 4,
                  borderWidth: 1,
                  borderColor: "#D9D2C5",
                  backgroundColor: COLORS.WHITE.hex,
                  fontFamily: "DMSans_400Regular",
                  fontSize: 13,
                  color: FOREST,
                  outlineStyle: "none",
                } as unknown as object}
              />
            </View>
            <FlatList
              data={availableMajors.filter((m) =>
                m.toLowerCase().includes(majorSearch.trim().toLowerCase())
              )}
              keyExtractor={(item) => item}
              keyboardShouldPersistTaps="handled"
              renderItem={({ item }) => {
                const isSelected = majors.has(item);
                return (
                  <Pressable
                    onPress={() => toggleMajor(item)}
                    style={({ hovered }: { hovered?: boolean }) => [
                      {
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 10,
                        paddingHorizontal: 16,
                        paddingVertical: 11,
                        borderBottomWidth: 1,
                        borderBottomColor: "rgba(217,210,197,0.55)",
                        backgroundColor:
                          hovered && Platform.OS === "web"
                            ? "rgba(100,127,77,0.06)"
                            : "transparent",
                      },
                      Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                    ]}
                  >
                    <View
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: 3,
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: isSelected ? COLORS.SAGE_DEEP.hex : "transparent",
                        borderWidth: isSelected ? 0 : 1.2,
                        borderColor: "rgba(100,127,77,0.55)",
                      }}
                    >
                      {isSelected ? (
                        <Svg width={9} height={9} viewBox="0 0 12 12" fill="none">
                          <Path
                            d="M2 6.5 L4.8 9 L10 3.5"
                            stroke={COLORS.WHITE.hex}
                            strokeWidth={1.7}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            fill="none"
                          />
                        </Svg>
                      ) : null}
                    </View>
                    <Text
                      style={{
                        fontFamily: isSelected ? "DMSans_600SemiBold" : "DMSans_400Regular",
                        fontSize: 13.5,
                        color: FOREST,
                        flex: 1,
                      }}
                    >
                      {item}
                    </Text>
                  </Pressable>
                );
              }}
              ListEmptyComponent={
                <Text
                  style={{
                    paddingVertical: 24,
                    textAlign: "center",
                    color: "rgba(28,43,26,0.45)",
                    fontFamily: "DMSans_400Regular",
                    fontSize: 12,
                  }}
                >
                  No matches
                </Text>
              }
            />
          </Pressable>
        </Pressable>
      </Modal>

      {/* Program Level + Fields of Competency pair into one row on ≥800px;
          they stack on narrow viewports so the form still works on mobile. */}
      <View className={sideBySide ? "mt-3.5 flex-row gap-4" : "mt-3.5"}>
        <View className={sideBySide ? "flex-1" : ""}>
          <Text className="mb-1 text-[10px] font-bold uppercase tracking-[0.16em] text-text/65">
            Program level<Text style={{ color: "#C0392B" }}> *</Text>
          </Text>
          <View className="flex-row flex-wrap gap-2">
            {PROGRAM_LEVELS.map((lvl) => (
              <Pill key={lvl} selected={programLevel === lvl} onPress={() => setProgramLevel(lvl)}>
                {lvl}
              </Pill>
            ))}
          </View>
        </View>

        <View className={sideBySide ? "flex-1" : "mt-3.5"}>
          <Text className="mb-1 text-[10px] font-bold uppercase tracking-[0.16em] text-text/65">
            Fields of competency
          </Text>
          {/* Searchable multi-select — same pattern as the major picker
              right above. Click the box to open a modal with a typeahead
              and a checklist of all 13 fields. */}
          <Pressable
        onPress={() => setCompModalOpen(true)}
        style={({ hovered }: { hovered?: boolean }) => [
          {
            minHeight: 44,
            borderRadius: 6,
            borderWidth: 1,
            borderColor:
              hovered && Platform.OS === "web" ? "rgba(28,43,26,0.42)" : "#D9D2C5",
            backgroundColor: COLORS.WHITE.hex,
            paddingHorizontal: 12,
            paddingVertical: 10,
            flexDirection: "row",
            alignItems: "center",
            gap: 10,
          },
          Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
        ]}
      >
        <View style={{ flex: 1, flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
          {competency.size === 0 ? (
            <Text
              style={{
                fontFamily: "DMSans_400Regular",
                fontSize: 13,
                color: "rgba(28,43,26,0.42)",
              }}
            >
              Choose your fields…
            </Text>
          ) : (
            FIELDS_OF_COMPETENCY.filter((f) => competency.has(f)).map((f) => (
              <View
                key={f}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 4,
                  paddingHorizontal: 8,
                  paddingVertical: 3,
                  borderRadius: 999,
                  backgroundColor: "rgba(100,127,77,0.14)",
                  borderWidth: 1,
                  borderColor: "rgba(100,127,77,0.45)",
                }}
              >
                <Text
                  style={{
                    fontFamily: "DMSans_500Medium",
                    fontSize: 11.5,
                    color: "#3F5A30",
                  }}
                >
                  {f}
                </Text>
              </View>
            ))
          )}
        </View>
        <Svg width={11} height={11} viewBox="0 0 12 12" fill="none">
          <Path
            d="M2 4 L6 8 L10 4"
            stroke="rgba(28,43,26,0.55)"
            strokeWidth={1.4}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </Svg>
      </Pressable>
        </View>
      </View>

      <Modal
        visible={compModalOpen}
        animationType="none"
        transparent
        onRequestClose={() => setCompModalOpen(false)}
      >
        <Pressable
          onPress={() => setCompModalOpen(false)}
          style={[
            {
              flex: 1,
              backgroundColor: "rgba(14,14,14,0.50)",
              alignItems: "center",
              justifyContent: "center",
              paddingHorizontal: 24,
            },
            Platform.OS === "web"
              ? ({
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                  cursor: "default",
                } as object)
              : null,
          ]}
        >
          <Pressable
            onPress={(e) => e.stopPropagation?.()}
            style={[
              {
                width: "100%",
                maxWidth: 460,
                maxHeight: "78%",
                backgroundColor: COLORS.LINEN.hex,
                borderRadius: 8,
                borderWidth: 1,
                borderColor: "#D9D2C5",
                overflow: "hidden",
              },
              Platform.OS === "web" ? ({ cursor: "default" } as unknown as object) : null,
            ]}
          >
            <View
              style={{
                paddingHorizontal: 16,
                paddingVertical: 14,
                backgroundColor: FOREST,
                flexDirection: "row",
                alignItems: "center",
                gap: 10,
              }}
            >
              <View style={{ flex: 1, alignItems: "flex-start" }}>
                <Text
                  style={{
                    fontFamily: "DMSans_700Bold",
                    fontSize: 11,
                    letterSpacing: 1.6,
                    textTransform: "uppercase",
                    color: COLORS.LINEN.hex,
                  }}
                >
                  Fields of competency
                </Text>
              </View>
              <Pressable
                onPress={() => setCompModalOpen(false)}
                accessibilityRole="button"
                accessibilityLabel="Close"
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                style={[
                  {
                    width: 26,
                    height: 26,
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 4,
                    borderWidth: 1,
                    borderColor: "rgba(247,244,238,0.35)",
                  },
                  Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                <Svg width={10} height={10} viewBox="0 0 12 12" fill="none">
                  <Path
                    d="M2 2 L10 10 M10 2 L2 10"
                    stroke={COLORS.LINEN.hex}
                    strokeWidth={1.4}
                    strokeLinecap="round"
                  />
                </Svg>
              </Pressable>
            </View>
            <View style={{ paddingHorizontal: 12, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: "#D9D2C5" }}>
              <TextInput
                value={compSearch}
                onChangeText={setCompSearch}
                placeholder="Search fields…"
                placeholderTextColor="rgba(28,43,26,0.42)"
                autoFocus
                style={{
                  paddingHorizontal: 12,
                  paddingVertical: 9,
                  borderRadius: 4,
                  borderWidth: 1,
                  borderColor: "#D9D2C5",
                  backgroundColor: COLORS.WHITE.hex,
                  fontFamily: "DMSans_400Regular",
                  fontSize: 13,
                  color: FOREST,
                  outlineStyle: "none",
                } as unknown as object}
              />
            </View>
            <FlatList
              data={FIELDS_OF_COMPETENCY.filter((f) =>
                f.toLowerCase().includes(compSearch.trim().toLowerCase())
              )}
              keyExtractor={(item) => item}
              keyboardShouldPersistTaps="handled"
              renderItem={({ item }) => {
                const isSelected = competency.has(item);
                return (
                  <Pressable
                    onPress={() => toggleCompetency(item)}
                    style={({ hovered }: { hovered?: boolean }) => [
                      {
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 10,
                        paddingHorizontal: 16,
                        paddingVertical: 12,
                        borderBottomWidth: 1,
                        borderBottomColor: "rgba(217,210,197,0.55)",
                        backgroundColor:
                          hovered && Platform.OS === "web"
                            ? "rgba(100,127,77,0.06)"
                            : "transparent",
                      },
                      Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                    ]}
                  >
                    <View
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: 3,
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: isSelected ? COLORS.SAGE_DEEP.hex : "transparent",
                        borderWidth: isSelected ? 0 : 1.2,
                        borderColor: "rgba(100,127,77,0.55)",
                      }}
                    >
                      {isSelected ? (
                        <Svg width={9} height={9} viewBox="0 0 12 12" fill="none">
                          <Path
                            d="M2 6.5 L4.8 9 L10 3.5"
                            stroke={COLORS.WHITE.hex}
                            strokeWidth={1.7}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            fill="none"
                          />
                        </Svg>
                      ) : null}
                    </View>
                    <Text
                      style={{
                        fontFamily: isSelected ? "DMSans_600SemiBold" : "DMSans_400Regular",
                        fontSize: 13.5,
                        color: FOREST,
                        flex: 1,
                      }}
                    >
                      {item}
                    </Text>
                  </Pressable>
                );
              }}
              ListEmptyComponent={
                <Text
                  style={{
                    paddingVertical: 24,
                    textAlign: "center",
                    color: "rgba(28,43,26,0.45)",
                    fontFamily: "DMSans_400Regular",
                    fontSize: 12,
                  }}
                >
                  No matches
                </Text>
              }
            />
          </Pressable>
        </Pressable>
      </Modal>

      <Text className="mb-1 mt-3.5 text-[10px] font-bold uppercase tracking-[0.16em] text-text/65">
        Est. graduation<Text style={{ color: "#C0392B" }}> *</Text>
      </Text>
      <View className="flex-row flex-wrap items-end gap-2">
        <View className="min-w-[200px] flex-1">
          <Text className="mb-1 text-[9px] font-bold uppercase tracking-wide text-text/50">Term</Text>
          <View className="flex-row gap-1">
            {GRAD_TERMS.map((t) => (
              <Pressable
                key={t}
                onPress={() => setGraduationTerm(t)}
                className="min-w-[72px] flex-1 rounded-btn border py-2.5"
                style={{
                  borderColor: graduationTerm === t ? AMBER : "#D9D2C5",
                  backgroundColor: graduationTerm === t ? "rgba(181,135,58,0.1)" : COLORS.WHITE.hex,
                }}
              >
                <Text
                  className="text-center text-[11px] font-semibold uppercase"
                  style={{
                    fontFamily: "DMSans_600SemiBold",
                    color: graduationTerm === t ? AMBER : "rgba(26,26,24,0.48)",
                  }}
                >
                  {t}
                </Text>
              </Pressable>
            ))}
          </View>
        </View>
        <View className="w-[88px]">
          <Input label="Year" value={graduationYear} onChangeText={setGraduationYear} keyboardType="number-pad" placeholder="2027" required />
        </View>
      </View>

      <View className="mt-3.5">
        <Input
          label="Password"
          value={password}
          onChangeText={setPassword}
          secureTextEntry
          placeholder={PASSWORD_PLACEHOLDER}
          required
        />
        <PasswordStrengthMeter password={password} />
      </View>
      <View className="mt-2.5">
        <Input
          label="Confirm password"
          value={confirmPassword}
          onChangeText={setConfirmPassword}
          secureTextEntry
          placeholder="Re-enter password"
          required
        />
        {confirmPassword.length > 0 ? (
          <Text
            className="mt-1 text-[11px]"
            style={{
              fontFamily: "DMSans_500Medium",
              color: confirmPassword === password ? GREEN_MATCH : RED_MISMATCH,
            }}
          >
            {confirmPassword === password ? "Passwords match" : "Passwords do not match"}
          </Text>
        ) : null}
      </View>

      {error ? (
        <Text className="mt-3 text-[12px]" style={{ color: "#C0392B", fontFamily: "DMSans_500Medium" }}>
          {error}
        </Text>
      ) : null}
      {confirmMsg ? (
        <Text className="mt-3 text-[12px]" style={{ color: SUCCESS, fontFamily: "DMSans_500Medium" }}>
          {confirmMsg}
        </Text>
      ) : null}

      <View className="mt-4">
        <Button onPress={onSubmit} disabled={busy}>
          {busy ? "Creating account…" : "Create account"}
        </Button>
      </View>

      <Text className="mt-3 text-center text-[12px] text-text/55" style={{ fontFamily: "DMSans_400Regular" }}>
        Already have an account?{" "}
        <Link href={"/login" as Href} className="text-amber" style={{ fontFamily: "DMSans_600SemiBold" }}>
          Log in
        </Link>
      </Text>
    </AuthCard>
  );
}
